"use client";

import React, { useRef, useEffect } from "react";
import Image from "next/image";
import styles from "./YouTubeIframePlayer.module.scss";
import {
  VideoSource,
  VideoMetadata,
  VideoPlayerConfig,
  VideoPlayerEvents,
  DeviceType,
} from "@interfaces/video";
import assetLoader from "@utils/internalImageLoder";
import { generateEmbedUrl, extractYouTubeVideoId } from "@utils/videoUtils";

interface YouTubeIframePlayerProps {
  video: VideoSource;
  metadata: VideoMetadata;
  config?: VideoPlayerConfig;
  events?: VideoPlayerEvents;
  deviceType: DeviceType;
  isVisible: boolean;
  className?: string;
}

const YouTubeIframePlayer: React.FC<YouTubeIframePlayerProps> = ({
  video,
  metadata,
  config = {},
  events = {},
  deviceType,
  isVisible,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const defaultConfig: VideoPlayerConfig = {
    autoplay: true,
    muted: false,
    controls: false, // Always show YouTube's native controls
    loop: false,
    preload: "metadata",
    showTitle: false, // Let YouTube handle the title
    showProgress: false, // Let YouTube handle progress
    showVolume: false, // Let YouTube handle volume
    showClose: true, // Keep close button for modal
    ...config,
  };

  useEffect(() => {
    if (!isVisible) return;

    // Call onReady when iframe loads
    const handleIframeLoad = () => {
      events.onReady?.();
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener("load", handleIframeLoad);
      return () => {
        iframe.removeEventListener("load", handleIframeLoad);
      };
    }
  }, [isVisible, events]);

  const videoId = extractYouTubeVideoId(video.url);
  if (!videoId) {
    return (
      <div className={`${styles.youtubePlayer} ${className || ""}`}>
        <div className={styles.errorOverlay}>
          <p>Invalid YouTube URL</p>
        </div>
      </div>
    );
  }

  const embedUrl = generateEmbedUrl(video, {
    autoplay: defaultConfig.autoplay,
    muted: defaultConfig.muted,
    controls: defaultConfig.controls,
  });

  return (
    <div
      className={`${styles.youtubePlayer} ${className || ""}`}
      data-device={deviceType}
    >
      <iframe
        ref={iframeRef}
        className={styles.iframe}
        src={embedUrl}
        frameBorder="0"
        allow="autoplay; encrypted-media;"
        title={metadata.title}
      />

      {/* Close Button - Only show if configured */}
      {defaultConfig.showClose && events.onClose && (
        <button
          className={styles.closeButton}
          onClick={events.onClose}
          aria-label="Close video"
        >
          <Image
            src={`${assetLoader()}/icons/close-icon.svg`}
            alt="Close"
            width={14}
            loading="lazy"
            height={14}
          />
        </button>
      )}
    </div>
  );
};

export default YouTubeIframePlayer;
